import React, {useMemo} from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  ToggleButton,
  Tooltip,
  Typography
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import DownloadIcon from "@mui/icons-material/Download"
import CompareArrowsIcon from "@mui/icons-material/CompareArrows"
import LMTable from "../components/Tables/LMTable"
import {LMColumn, PaginationModel, SortModel} from "../components/Tables/types"
import PageTitle from "../components/subcomponents/PageTitle"
import {styled} from "@mui/material/styles"
import {useNavigate} from "react-router-dom"
import {DatePicker} from "@mui/x-date-pickers/DatePicker"
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider"
import {deDE} from "@mui/x-date-pickers/locales"
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, {Dayjs} from "dayjs"
import {useTranslation} from "react-i18next"
import {getLowImplantsData, downloadLowImplantsExcel, LowImplantsRow} from "../api/customers"
import {getTmzReportData, TmzReportData, TmzNotResponsiveRow, TmzNoPatientDataRow} from "../api/software"
import ErrorExclamation from "../components/subcomponents/ErrorExclamation"
import SalesPersonMap from "../components/SalesPersonMap"
import SalesPersonMetricsTable from "../components/SalesPersonMetricsTable"
import {getSalesPersonMapData, SalesPersonMapData} from "../api/salesperson"
import {compareCustomerNumbers, CUSTOMER_NUMBER_SORT_MODEL} from "../utils/customerNumberSort"
import {berlinToday} from "../utils/berlinDate"

const TableBox = styled(Box)({
  padding: "0.5rem 0"
})

/**
 * Default date for the salesperson map: the last day of the previous month, unless today is
 * itself the last day of a month — in which case today is used. Mirrors the server's
 * getDefaultMapDate (Europe/Berlin) so the precomputed (cached) view is what loads by default.
 */
function defaultMapDate(): Dayjs {
  const today = berlinToday()
  return today.date() === today.daysInMonth() ? today : today.subtract(1, "month").endOf("month")
}

export default function ReportPage() {
  const {t, i18n} = useTranslation()
  // The date pickers' own built-in strings (the "Today"/"Cancel"/"OK" action bar, etc.) aren't
  // covered by our i18next translations — MUI ships its own locale bundle for those.
  const pickerLocaleText = i18n.language.startsWith("de")
    ? deDE.components.MuiLocalizationProvider.defaultProps.localeText
    : undefined
  const navigate = useNavigate()

  const [rows, setRows] = React.useState<LowImplantsRow[]>([])
  const [error, setError] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [pagination, setPagination] = React.useState<PaginationModel>({page: 0, pageSize: 25})
  const [sortModel, setSortModel] = React.useState<SortModel>(CUSTOMER_NUMBER_SORT_MODEL)
  const [selectedDate, setSelectedDate] = React.useState<Dayjs>(dayjs())

  const [mapData, setMapData] = React.useState<SalesPersonMapData | null>(null)
  const [mapError, setMapError] = React.useState(false)
  const [mapLoading, setMapLoading] = React.useState(false)
  const [mapDate, setMapDate] = React.useState<Dayjs>(defaultMapDate)

  const [compareEnabled, setCompareEnabled] = React.useState(false)
  const [compareDate, setCompareDate] = React.useState<Dayjs | null>(null)
  const [compareData, setCompareData] = React.useState<SalesPersonMapData | null>(null)
  const [compareLoading, setCompareLoading] = React.useState(false)

  const [tmzData, setTmzData] = React.useState<TmzReportData | null>(null)
  const [tmzError, setTmzError] = React.useState(false)
  const [tmzLoading, setTmzLoading] = React.useState(false)
  const [tmzNotResponsivePagination, setTmzNotResponsivePagination] = React.useState<PaginationModel>({page: 0, pageSize: 25})
  const [tmzNoPatientDataPagination, setTmzNoPatientDataPagination] = React.useState<PaginationModel>({page: 0, pageSize: 25})

  const columns: LMColumn<LowImplantsRow>[] = useMemo(() => [
    {field: "name", headerName: "report.columns.customerName", width: 200, renderCell: (params) => (
      <Link component="button" onClick={() => navigate(`/customers/update/${params.row.customerId}`)}>
        {params.value}
      </Link>
    )},
    {field: "street", headerName: "report.columns.street", width: 160},
    {field: "postalCode", headerName: "report.columns.postalCode", width: 80},
    {field: "city", headerName: "report.columns.city", width: 140},
    {field: "tmzActiveSince", headerName: "report.columns.tmzActiveSince", width: 150},
    {field: "pauseUntil", headerName: "report.columns.pauseUntil", width: 130},
    {field: "plannedDeactivationDate", headerName: "report.columns.plannedDeactivationDate", width: 150},
    {
      field: "customerNumber",
      headerName: "report.columns.customerNumber",
      width: 150,
      sortComparator: compareCustomerNumbers,
    },
    {field: "eTin", headerName: "report.columns.eTin", width: 120},
    {
      field: "biotronikCustomerNumber",
      headerName: "report.columns.biotronikCustomerNumber",
      width: 120,
      sortComparator: compareCustomerNumbers,
    },
    {field: "externalSensors", headerName: "report.columns.externalSensors", width: 150, type: "number"},
    {field: "implants", headerName: "report.columns.implants", width: 100, type: "number"},
  ], [t])

  const tmzNotResponsiveColumns: LMColumn<TmzNotResponsiveRow>[] = useMemo(() => [
    {field: "customerName", headerName: "report.tmz.columns.customerName", width: 220, renderCell: (params) => (
      <Link component="button" onClick={() => navigate(`/customers/update/${params.row.customerId}`)}>
        {params.value}
      </Link>
    )},
    {field: "url", headerName: "report.tmz.columns.url", width: 280},
    {field: "unresponsiveFor", headerName: "report.tmz.columns.unresponsiveFor", width: 180},
  ], [t])

  const tmzNoPatientDataColumns: LMColumn<TmzNoPatientDataRow>[] = useMemo(() => [
    {field: "customerName", headerName: "report.tmz.columns.customerName", width: 220, renderCell: (params) => (
      <Link component="button" onClick={() => navigate(`/customers/update/${params.row.customerId}`)}>
        {params.value}
      </Link>
    )},
    {field: "url", headerName: "report.tmz.columns.url", width: 280},
  ], [t])

  React.useEffect(() => {
    loadTmzData().then()
  }, [])

  React.useEffect(() => {
    loadData().then()
  }, [selectedDate])

  React.useEffect(() => {
    // Guard against out-of-order responses: if the date changes (or the component unmounts)
    // before this request resolves, its results are ignored, so a slow older request can't
    // overwrite a newer one's map.
    let active = true
    setMapLoading(true)
    // Clear any prior error so retrying (a corrected date, or a transient failure) shows the
    // loading spinner rather than leaving the stale error banner up for the whole new request.
    setMapError(false)
    getSalesPersonMapData(mapDate.format("YYYY-MM-DD"))
      .then(data => { if (active) { setMapData(data); setMapError(false) } })
      .catch(e => { if (active) { console.error(e); setMapError(true) } })
      .finally(() => { if (active) setMapLoading(false) })
    return () => { active = false }
  }, [mapDate])

  React.useEffect(() => {
    if (!compareEnabled || !compareDate) {
      setCompareData(null)
      return
    }
    let active = true
    setCompareLoading(true)
    getSalesPersonMapData(compareDate.format("YYYY-MM-DD"))
      .then(data => { if (active) setCompareData(data) })
      .catch(e => { if (active) { console.error(e); setCompareData(null) } })
      .finally(() => { if (active) setCompareLoading(false) })
    return () => { active = false }
  }, [compareEnabled, compareDate])

  async function loadData() {
    setLoading(true)
    try {
      const date = selectedDate.isSame(dayjs(), 'day') ? undefined : selectedDate.format('YYYY-MM-DD')
      setRows(await getLowImplantsData(date))
      setError(false)
    } catch (e) {
      console.error(e)
      setError(true)
    }
    setLoading(false)
  }

  async function loadTmzData() {
    setTmzLoading(true)
    try {
      setTmzData(await getTmzReportData())
      setTmzError(false)
    } catch (e) {
      console.error(e)
      setTmzError(true)
    }
    setTmzLoading(false)
  }

  async function handleExcelDownload(e: React.MouseEvent) {
    e.stopPropagation()
    try {
      const date = selectedDate.isSame(dayjs(), 'day') ? undefined : selectedDate.format('YYYY-MM-DD')
      const response = await downloadLowImplantsExcel(date)
      const blob = await response.blob()

      const disposition = response.headers.get("Content-Disposition")
      let filename = `customers_low_implants_${new Date().toISOString().slice(0, 10)}.xlsx`
      if (disposition?.includes("filename=")) {
        const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition)
        if (match?.[1]) filename = match[1].replace(/['"]/g, "")
      }

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className={"base-container"}>
      <PageTitle>{t("appBar.reporting")}</PageTitle>
      <Divider/>

      <Accordion defaultExpanded sx={{marginTop: "2rem"}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>} id="low-implants-panel">
          <Typography variant="h6" sx={{fontWeight: 600}}>
            {t("report.tableTitle")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2, mb: 1}}>
            <LocalizationProvider dateAdapter={AdapterDayjs} localeText={pickerLocaleText}>
              <DatePicker
                disableFuture
                format="DD/MM/YYYY"
                value={selectedDate}
                onChange={v => setSelectedDate(v || dayjs())}
                slotProps={{textField: {size: "small", label: t("report.datePicker.label")}}}
              />
            </LocalizationProvider>
            <Button
              variant="contained"
              size="small"
              onClick={handleExcelDownload}
              startIcon={<DownloadIcon/>}
            >{t("pages.exportLowImplantsExcel")}</Button>
          </Box>
          <TableBox>
            {error
              ? <ErrorExclamation message={t("activities.dataFetchFail")}/>
              : <LMTable<LowImplantsRow>
                rows={rows}
                columns={columns}
                getRowId={row => row.customerNumber}
                paginationMode="client"
                paginationModel={pagination}
                onPaginationModelChange={setPagination}
                pageSizeOptions={[10, 25, 50]}
                options={{sortModel}}
                onSortModelChange={setSortModel}
                loading={loading}
                selectionMode="none"
              />
            }
          </TableBox>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded sx={{marginTop: "1rem"}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>} id="salesperson-map-panel">
          <Typography variant="h6" sx={{fontWeight: 600}}>
            {t("map.sectionTitle")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, mb: 1, flexWrap: "wrap"}}>
            <LocalizationProvider dateAdapter={AdapterDayjs} localeText={pickerLocaleText}>
              <DatePicker
                maxDate={berlinToday()}
                format="DD/MM/YYYY"
                value={mapDate}
                onChange={v => {
                  // Ignore invalid/incomplete input — an invalid Dayjs is a truthy object and
                  // would be sent to the server as "Invalid Date" -> 400. Clearing resets to default.
                  // maxDate only styles the calendar popup; it does not block a keyboard-typed
                  // future date, so also reject anything after today (the server 400s on it).
                  if (v == null) setMapDate(defaultMapDate())
                  else if (v.isValid() && !v.isAfter(berlinToday(), "day")) setMapDate(v)
                }}
                // "Today" lives in the calendar popup itself (no separate button needed);
                // "Accept"/"Cancel" keep the usual confirm/dismiss behaviour.
                slotProps={{
                  textField: {size: "small", label: t("map.datePicker.label")},
                  actionBar: {actions: ["today", "cancel", "accept"]},
                }}
              />
            </LocalizationProvider>

            {!mapDate.isSame(defaultMapDate(), "day") && (
              <Button size="small" variant="text" onClick={() => setMapDate(defaultMapDate())}>
                {t("map.lastDayLastMonth")}
              </Button>
            )}

            <Tooltip title={t("map.showComparison")}>
              <ToggleButton
                value="compare"
                selected={compareEnabled}
                size="small"
                onChange={() => {
                  const next = !compareEnabled
                  setCompareEnabled(next)
                  if (next && compareDate === null) setCompareDate(berlinToday())
                }}
              >
                <CompareArrowsIcon fontSize="small"/>
              </ToggleButton>
            </Tooltip>

            {compareEnabled && (
              <LocalizationProvider dateAdapter={AdapterDayjs} localeText={pickerLocaleText}>
                <DatePicker
                  maxDate={berlinToday()}
                  format="DD/MM/YYYY"
                  value={compareDate}
                  onChange={v => {
                    if (v == null) setCompareDate(berlinToday())
                    else if (v.isValid() && !v.isAfter(berlinToday(), "day")) setCompareDate(v)
                  }}
                  slotProps={{
                    textField: {size: "small", label: t("map.compareDate")},
                    actionBar: {actions: ["today", "cancel", "accept"]},
                  }}
                />
              </LocalizationProvider>
            )}
          </Box>
          {mapError
            ? <ErrorExclamation message={t("activities.dataFetchFail")}/>
            : (mapLoading || !mapData)
              ? <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 2, py: 5}}>
                  <CircularProgress size={22}/>
                  <Typography variant="body2" color="text.secondary">{t("map.loading")}</Typography>
                </Box>
              : <>
                  {compareEnabled && compareLoading && (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                      <CircularProgress size={14}/>
                      <Typography variant="caption" color="text.secondary">{t("map.loading")}</Typography>
                    </Box>
                  )}
                  <SalesPersonMap data={mapData} compareData={compareEnabled ? compareData : undefined}/>
                </>
          }
          {/*
            Owns its own two comparison dates and its metric mode, moving independently of the
            map above — but its "last month" side defaults to the same date the map itself
            defaults to, so the map's own fetch is passed down for it to reuse while the two
            dates line up, instead of firing a second identical request.
          */}
          <SalesPersonMetricsTable mapData={mapData} mapDate={mapDate} mapLoading={mapLoading} mapError={mapError}/>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded sx={{marginTop: "1rem"}}>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>} id="tmz-report-panel">
          <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
            <Typography variant="h6" sx={{fontWeight: 600}}>
              {t("report.tmz.sectionTitle")}
            </Typography>
            {tmzData != null && (
              <Typography variant="body2" color="text.secondary">
                {t("report.tmz.total", {count: tmzData.total})}
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {tmzError ? (
            <ErrorExclamation message={t("activities.dataFetchFail")}/>
          ) : (
            <TableBox>
              {tmzData?.noDataAtAll && (
                // Same warning the emailed report carries: when nothing came through, say so
                // instead of leaving every instance listed below as if it were patient-free.
                <Alert severity="warning" sx={{mb: 2}}>
                  <Typography variant="body2" sx={{fontWeight: 600}}>
                    {t("report.tmz.noDataWarning.title")}
                  </Typography>
                  <Typography variant="body2">
                    {t("report.tmz.noDataWarning.body", {count: tmzData.total})}
                    {tmzData.noPatientData.length > 0 ? ` ${t("report.tmz.noDataWarning.listCaveat")}` : ""}
                    {` ${t("report.tmz.noDataWarning.followUp")}`}
                  </Typography>
                </Alert>
              )}
              <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
                {t("report.tmz.notResponsiveTitle")}
              </Typography>
              <LMTable<TmzNotResponsiveRow>
                rows={tmzData?.notResponsive ?? []}
                columns={tmzNotResponsiveColumns}
                getRowId={row => row.url}
                paginationMode="client"
                paginationModel={tmzNotResponsivePagination}
                onPaginationModelChange={setTmzNotResponsivePagination}
                pageSizeOptions={[10, 25, 50]}
                loading={tmzLoading}
                selectionMode="none"
              />

              <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1, mt: 2}}>
                {t("report.tmz.noPatientDataTitle")}
              </Typography>
              <LMTable<TmzNoPatientDataRow>
                rows={tmzData?.noPatientData ?? []}
                columns={tmzNoPatientDataColumns}
                getRowId={row => row.url}
                paginationMode="client"
                paginationModel={tmzNoPatientDataPagination}
                onPaginationModelChange={setTmzNoPatientDataPagination}
                pageSizeOptions={[10, 25, 50]}
                loading={tmzLoading}
                selectionMode="none"
              />
            </TableBox>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  )
}
